import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ShortUniqueId from 'short-unique-id';
import { CSSProperties } from 'styled-components';
import { useLoader } from 'contexts/Loader';
import { useReCAPTCHA } from 'contexts/ReCAPTCHAContext';
import { api } from 'services/apiClient';
import { getColorDistance, hexToRgb, similarityThreshold } from 'utils/colors';
import { CropperSizeType, IMessageProps, IPhotosPool, Paginate, Pool, PoolCategory } from './types';

const { randomUUID } = new ShortUniqueId({ length: 10 });

export const states = [
  { label: 'Acre', value: 'AC' },
  { label: 'Alagoas', value: 'AL' },
  { label: 'Amapá', value: 'AP' },
  { label: 'Amazonas', value: 'AM' },
  { label: 'Bahia', value: 'BA' },
  { label: 'Ceará', value: 'CE' },
  { label: 'Distrito Federal', value: 'DF' },
  { label: 'Espírito Santo', value: 'ES' },
  { label: 'Goiás', value: 'GO' },
  { label: 'Maranhão', value: 'MA' },
  { label: 'Mato Grosso', value: 'MT' },
  { label: 'Mato Grosso do Sul', value: 'MS' },
  { label: 'Minas Gerais', value: 'MG' },
  { label: 'Pará', value: 'PA' },
  { label: 'Paraíba', value: 'PB' },
  { label: 'Paraná', value: 'PR' },
  { label: 'Pernambuco', value: 'PE' },
  { label: 'Piauí', value: 'PI' },
  { label: 'Rio de Janeiro', value: 'RJ' },
  { label: 'Rio Grande do Norte', value: 'RN' },
  { label: 'Rio Grande do Sul', value: 'RS' },
  { label: 'Rondônia', value: 'RO' },
  { label: 'Roraima', value: 'RR' },
  { label: 'Santa Catarina', value: 'SC' },
  { label: 'São Paulo', value: 'SP' },
  { label: 'Sergipe', value: 'SE' },
  { label: 'Tocantins', value: 'TO' },
];

export const usePoolState = (isGuest?: boolean, poolInfo?: Pool) => {
  const { handleVerifyRecaptcha, recaptcha, refreshRecaptcha, setRefreshRecaptcha } = useReCAPTCHA();
  const [modalImageCropper, setModalImageCropper] = useState(false);
  const [cropperSize, setCropperSize] = useState<CropperSizeType>('cover');
  const [newBackGroundColor, setNewBackGroundColor] = useState('#fff6e8');
  const [messages, setMessages] = useState<IMessageProps[]>([]);
  const targetColor = { r: 255, g: 255, b: 255 };
  const [imgsPool, setImgsPool] = useState<IPhotosPool[]>([]);
  const [submit, setSubmit] = useState(false);
  const [deviceWidth, setDeviceWidth] = useState(0);
  const { setLoader, setLabel } = useLoader();
  const [coverImage, setCoverImage] = useState<File>();
  const [footerImage, setFooterImage] = useState<File>();
  const [formData, setFormData] = useState<Pool>({
    slug: randomUUID(),
    fixedFee: 1,
  });
  const [pool, setPool] = useState<Pool>({} as Pool);
  const route = useRouter();
  const [modalSocialShare, setModalSocialShare] = useState(false);
  const [albumStaticImages, setAlbumStaticImages] = useState<File[]>([]);
  const [snapData, setSnapData] = useState(
    JSON.stringify({
      slug: formData.slug,
      fixedFee: formData.fixedFee,
    }),
  );
  const [titleStyles, setTitleStyles] = useState<CSSProperties>();
  const [isValidSlug, setIsValidSlug] = useState<boolean>(true);

  const [messageForm, setMessageForm] = useState<{
    name?: string;
    content?: string;
  }>({});

  const [categories, setCategories] = useState<PoolCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>('');

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get<PoolCategory[]>(`pool-categories`);
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const onCategoryChanged = useCallback((e: React.SyntheticEvent<HTMLSelectElement, Event>) => {
    setSelectedCategoryId(e.currentTarget.value);
  }, []);

  useEffect(() => {
    poolInfo?.category?.id && setSelectedCategoryId(poolInfo?.category?.id);
  }, [poolInfo]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handlerCropImage = (file: File) => {
    switch (cropperSize) {
      case 'cover':
        setCoverImage(file);
        break;
      case 'footer':
        setFooterImage(file);
        break;
      case 'album':
        if (!poolInfo?.id) {
          setAlbumStaticImages((prev) => {
            return [...prev, file];
          });
          setImgsPool((prev) => {
            return [
              ...prev,
              {
                id: (prev.length + 1).toString(),
                partyId: '',
                url: URL.createObjectURL(file),
              },
            ];
          });
        } else handleFileChange(file);
        break;
    }
  };
  const handleFileChange = useCallback(
    async (photo: File, poolId?: string | number, index?: number) => {
      setLoader?.(true);
      setLabel?.(index ? `Adicionando foto(${index}) no album` : 'Adicionando foto no album');
      try {
        if (!photo) throw new Error('Nenhum arquivo selecionado');
        const newFile = photo;
        const file = new FormData();
        file.append('photo', newFile);

        await api
          .post(`pools/${poolInfo?.id || poolId}/photos`, file)
          .then(({ data }) => {
            setImgsPool((oldState) => {
              return [...oldState, data];
            });
            toast('Foto adicionada', {
              type: 'success',
            });
          })
          .catch((error: { userMessage?: string }) => {
            toast(error?.userMessage || 'Atualização de dados falhou', {
              type: 'error',
            });
          })
          .finally(() => {
            setLoader?.(false);
          });
      } catch (err: any) {
        console.log(err);
      }
    },
    [poolInfo?.id, setImgsPool],
  );

  const handleCoverChange = useCallback(
    async (poolId: string) => {
      const photo = coverImage;

      if (!photo) return;

      try {
        setLoader?.(true);
        setLabel?.('Salvando a imagem de capa...');
        if (!photo) throw new Error('Nenhum arquivo selecionado');
        const newFile = photo;
        const file = new FormData();
        file.append('cover', newFile);

        await api.patch(`pools/${poolId}/cover`, file);
      } catch (error: any) {
        const mensagem = (error as { userMessage?: string })?.userMessage || error?.response?.data?.message || error?.message || 'Erro ao salvar a imagem de capa.';
        toast(mensagem, {
          type: 'error',
          style: { fontSize: 14 },
        });
      }
    },
    [coverImage],
  );

  const handleFooterChange = useCallback(
    async (poolId: string) => {
      const photo = footerImage;

      try {
        setLoader?.(true);
        setLabel?.('Salvando a imagem do rodape...');
        if (!photo) throw new Error('Nenhum arquivo selecionado');
        const newFile = photo;
        const file = new FormData();
        file.append('footer', newFile);

        await api.patch(`pools/${poolId}/footer`, file);
      } catch (error: any) {
        const mensagem = (error as { userMessage?: string })?.userMessage || error?.response?.data?.message || error?.message || 'Erro ao salvar a imagem do rodapé.';
        toast(mensagem, {
          type: 'error',
          style: { fontSize: 14 },
        });
      }
    },
    [footerImage],
  );

  const getCropperSize = () => {
    return {
      cover: {
        height: 800,
        width: 1920,
      },
      album: {
        width: 1080,
        height: 1080,
      },
      'lateral-l': {
        width: 314,
        height: 500,
      },
      'lateral-r': {
        width: 314,
        height: 500,
      },
      footer: {
        height: 700,
        width: 1920,
      },
    }[cropperSize];
  };

  const toggleModalImageCropper = (type: CropperSizeType) => {
    setCropperSize(type);
    setModalImageCropper((prev) => !prev);
  };

  const [valueOptions, setValueOptions] = useState<number[]>([]);
  const [deafutlValueOptions, setDefaultValueOptions] = useState([15, 30, 50, 100, 150]);

  useEffect(() => {
    // Function to handle device width change
    const handleResize = () => {
      setDeviceWidth(window.innerWidth);
    };
    setDeviceWidth(window.innerWidth);
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { fixedFee, ...formDataWithoutFixedFee } = formData;

  const handlerEditPool = useCallback(async () => {
    if (!poolInfo?.id) return;
    try {
      setLoader?.(true);
      setLabel?.('Editando a vaquinha');

      // Criar uma cópia do objeto formData sem o campo fixedFee
      const { fixedFee, fontColor: font_color, fontFamily: font_family, videoUrl: video_url, ...dt } = formData;

      // Converter o valor do campo target para o formato esperado pela API
      dt.target = parseFloat(formData.target as string) * 100;

      if (newBackGroundColor) {
        dt.backgroundColor = newBackGroundColor;
      }

      await api
        .patch<Pool>(`pools/${poolInfo?.id}`, {
          ...dt,
          font_color: font_color ?? '',
          font_family: font_family ?? '',
          video_url,
        })
        .then(async () => {
          coverImage && poolInfo.id && (await handleCoverChange(poolInfo?.id));
          footerImage && poolInfo.id && (await handleFooterChange(poolInfo?.id));

          setPool({
            ...pool,
            ...dt,
            target: parseFloat((dt.target || '0')?.toString()) / 100,
            fontColor: formData.fontColor,
            fontFamily: formData.fontFamily,
            videoUrl: formData.videoUrl ?? '',
            state: formData.state,
          });

          setSnapData(
            JSON.stringify({
              ...formData,
            }),
          );

          toast('Vaquinha editada com sucesso', {
            type: 'success',
          });
        })
        .catch((error: { userMessage?: string; response?: { data?: { message?: string } } }) => {
          const mensagem = error?.userMessage || error?.response?.data?.message || 'Edição da vaquinha falhou.';
          toast(mensagem, { type: 'error', style: { fontSize: 14 } });
        })
        .finally(() => {
          setLoader?.(false);
          setSubmit(false);
        });
    } catch (error) {
      console.log(error);
    }
  }, [
    poolInfo?.id,
    setLoader,
    setLabel,
    formData,
    newBackGroundColor,
    coverImage,
    handleCoverChange,
    footerImage,
    handleFooterChange,
    pool,
  ]);

  const handlerPool = useCallback(async () => {
    try {
      if (!poolInfo?.id) {
        setLoader?.(true);

        const [font_color, font_family, video_url] = [formData.fontColor ?? '', formData.fontFamily ?? '', formData.videoUrl ?? ''];

        delete formData.fontColor;
        delete formData.fontFamily;

        delete formDataWithoutFixedFee.fontColor;
        delete formDataWithoutFixedFee.fontFamily;
        delete formDataWithoutFixedFee.videoUrl;

        await api
          .post<Pool>(`pools`, {
            ...formDataWithoutFixedFee,
            target: parseFloat(formData.target as string) * 100,
            background_color: newBackGroundColor,
            font_color,
            font_family,
            category_id: selectedCategoryId,
            video_url,
          })
          .then(async ({ data }: any) => {
            coverImage && (await handleCoverChange(data?.id));
            footerImage && (await handleFooterChange(data?.id));

            if (albumStaticImages && data?.id) {
              albumStaticImages.map((file, index) => handleFileChange(file, data.id, index + 1));
            }

            toast('Vaquinha criada com sucesso', {
              type: 'success',
            });
            setLoader?.(false);
            route.replace('/pool/' + data.slug);
          })
          .catch((error: { userMessage?: string; response?: { data?: { message?: string } } }) => {
            const mensagem = error?.userMessage || error?.response?.data?.message || 'Criação da vaquinha falhou.';
            toast(mensagem, { type: 'error', style: { fontSize: 14 } });
            setLoader?.(false);
          });
      } else handlerEditPool();
    } catch (error) {
      console.log(error);
    } finally {
      setLoader?.(false);
    }
  }, [
    poolInfo?.id,
    handlerEditPool,
    setLoader,
    formData.fontColor,
    formData.fontFamily,
    formData.target,
    formData.videoUrl,
    formDataWithoutFixedFee,
    newBackGroundColor,
    selectedCategoryId,
    coverImage,
    handleCoverChange,
    footerImage,
    handleFooterChange,
    albumStaticImages,
    route,
    handleFileChange,
  ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        (formData.title && formData.target && formData.description && !pool.id) ||
        (poolInfo?.id && (snapData !== JSON.stringify(formData) || coverImage || footerImage)) ||
        (poolInfo?.id && newBackGroundColor && newBackGroundColor !== pool.backgroundColor) ||
        (poolInfo?.id && (poolInfo?.category?.id ?? '') !== selectedCategoryId)
      ) {
        setSubmit(true);
      } else setSubmit(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [
    poolInfo?.id,
    formData,
    valueOptions,
    newBackGroundColor,
    snapData,
    footerImage,
    pool.id,
    pool.backgroundColor,
    coverImage,
    poolInfo?.category?.id,
    selectedCategoryId,
  ]);

  const distance = getColorDistance(targetColor, hexToRgb(newBackGroundColor || ''));

  const fontColor = useMemo(() => (distance < similarityThreshold ? '#174F68' : '#ffffff'), [distance]);

  const handlerSendMessage = useCallback(
    async (poolId: string) => {
      try {
        setLoader?.(true);
        await api
          .post<IMessageProps>(`pools/${poolId}/messages`, {
            ...messageForm,
          })
          .then(async ({ data }: any) => {
            toast('Mensagem enviada com sucesso', {
              type: 'success',
            });
            setMessageForm({
              content: '',
              name: '',
            });
            setMessages([...messages, data]);
            setLoader?.(false);
          })
          .catch((error: { userMessage?: string; response?: { data?: { message?: string } } }) => {
            const mensagem = error?.userMessage || error?.response?.data?.message || 'Envio da mensagem falhou.';
            toast(mensagem, { type: 'error', style: { fontSize: 14 } });
            setLoader?.(false);
          });
      } catch (error) {
        console.log(error);
      } finally {
        setLoader?.(false);
      }
    },
    [messageForm, messages],
  );

  const handleModerateMessage = useCallback(
    async (messageId: string, visible: boolean) => {
      try {
        await api.patch(`pools/${poolInfo?.id}/messages/${messageId}/toggle_visible`);
        if (visible) {
          setMessages((previous) => previous.map((item) => (item.id === messageId ? { ...item, visible: false } : item)));
        }
        if (!visible) {
          setMessages((previous) => previous.map((item) => (item.id === messageId ? { ...item, visible: true } : item)));
        }
      } catch (error) {
        console.log(error);
      }
    },
    [poolInfo?.id],
  );

  useEffect(() => {
    if (!poolInfo?.id) return;
    if (isGuest) {
      api.get<Paginate<IMessageProps>>(`pools/${poolInfo?.id}/messages`).then(({ data }) => {
        setMessages(data.values);
      });
    } else {
      api.get<Paginate<IMessageProps>>(`pools/${poolInfo?.id}/messages/all`).then(({ data }) => {
        setMessages(data.values);
      });
    }

    api.get<Paginate<IPhotosPool>>(`pools/${poolInfo?.id}/photos`).then(({ data }) => {
      setImgsPool(data.values);
    });
  }, [isGuest, poolInfo?.id]);

  const handleRemoveImg = useCallback(
    async (id: string) => {
      try {
        if (poolInfo?.id) {
          setLoader?.(true);
          setLabel?.('Removendo a foto do album');
          await api
            .delete(`pools/${poolInfo?.id}/photos/${id}`)
            .then(({ data }) => {
              setImgsPool((oldState) => oldState.filter((i) => i.id !== id));
              toast('Foto removida com sucesso', {
                type: 'success',
              });
            })
            .catch((error: { userMessage?: string }) => {
              toast(error?.userMessage || 'Atualização de dados falhou', { type: 'error' });
            })
            .finally(() => {
              setLoader?.(false);
            });
        } else {
          setAlbumStaticImages((prev) => {
            return prev.filter((_, index) => (index + 1).toString() !== id);
          });
          setImgsPool((prev) => {
            return prev.filter((item) => item.id !== id);
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [poolInfo?.id, setImgsPool, imgsPool, albumStaticImages],
  );

  const handlerShareData = useCallback(async () => {
    if (!poolInfo) return;
    try {
      /*await navigator.share({
        title: 'Vaquinha Meu Nascimento | ' + poolInfo?.title,
        text: poolInfo?.description,
        url: window.location.origin + '/' + poolInfo?.slug,
      });*/
      setModalSocialShare(true);
    } catch (err) {
      toast(`[${err}] vaquinha não compartilhada`, {
        type: 'error',
      });
    }
  }, [poolInfo]);

  const handlerTitleStyles = (value: CSSProperties) => setTitleStyles(value);

  const formatEventStyle = useMemo(() => {
    if (!pool.fontFamily) return;
    else return JSON.parse(pool.fontFamily || '') as Pick<CSSProperties, 'fontFamily' | 'textDecoration' | 'fontStyle' | 'fontWeight'>;
  }, [pool]);

  useEffect(() => {
    if (
      !formatEventStyle &&
      titleStyles?.fontFamily === 'P22 Mackinac Pro' &&
      titleStyles.color === '#8B8EE8' &&
      titleStyles.fontWeight === 'normal' &&
      titleStyles.textDecoration === 'none' &&
      titleStyles.fontStyle === 'normal'
    )
      return;

    const stringStyle = JSON.stringify({
      fontFamily: titleStyles?.fontFamily,
      textDecoration: titleStyles?.textDecoration,
      fontStyle: titleStyles?.fontStyle,
      fontWeight: titleStyles?.fontWeight,
    });

    if (stringStyle === '{}') return;
    if (stringStyle !== pool.fontFamily || pool.fontColor !== titleStyles?.color)
      setFormData({
        ...formData,
        fontColor: titleStyles?.color,
        fontFamily: stringStyle,
      });
  }, [formatEventStyle, titleStyles, pool]);

  useEffect(() => {
    if (!pool) {
      handlerTitleStyles({
        fontFamily: 'P22 Mackinac Pro',
        color: '#8B8EE8',
      });
    } else
      handlerTitleStyles({
        color: pool.fontColor,
        ...formatEventStyle,
      });
  }, [formatEventStyle]);

  const handleVerifySlug = useCallback(
    (value: string) => {
      setLoader?.(true);
      setLabel?.('verificando a slug!...');
      api
        .get(`pools/verify_slug/${value}`)
        .then(({ data: res }) => {
          setIsValidSlug(res?.available);
        })
        .catch(() => setIsValidSlug(false))
        .finally(() => setLoader?.(false));
    },
    [setLabel, setLoader],
  );

  useEffect(() => {
    if (poolInfo) {
      if (poolInfo.status) {
        setPool(prev => ({
          ...prev,
          ...poolInfo,
          active: poolInfo.status !== 'finished'
        }));
      }
    }
  }, [poolInfo, setPool]);

  const reloadPoolData = useCallback(async () => {
    if (!poolInfo?.slug) return;
    try {
      const response = await api.get(`pools/${poolInfo.slug}`);

      const mappedData = {
        ...response.data,
        active: response.data.status !== 'finished'
      };

      setPool(mappedData);
      return mappedData;
    } catch (error) {
      return null;
    }
  }, [poolInfo?.slug, setPool]);

  const handleFinishCampaign = useCallback(async () => {
    if (!poolInfo?.id) return;
    try {
      setLoader?.(true);
      setLabel?.('Finalizando a campanha...');

      const response = await api.patch(`pools/${poolInfo.id}/finish`);

      setPool((prev) => ({ ...prev, active: false, status: 'finished' }));

      const reloadedData = await reloadPoolData();

      toast('Campanha finalizada com sucesso', {
        type: 'success',
      });

      return true;
    } catch (error) {
      toast('Erro ao finalizar a campanha', {
        type: 'error',
      });
      return false;
    } finally {
      setLoader?.(false);
    }
  }, [poolInfo?.id, setLoader, setLabel, setPool, reloadPoolData]);

  return {
    onCategoryChanged,
    categories,
    modalSocialShare,
    setModalSocialShare,
    pool,
    setPool,
    handlerShareData,
    handleRemoveImg,
    imgsPool,
    setImgsPool,
    handlerSendMessage,
    messageForm,
    setMessageForm,
    cropperSize,
    footerImage,
    fontColor,
    newBackGroundColor,
    setNewBackGroundColor,
    valueOptions,
    setValueOptions,
    deafutlValueOptions,
    setDefaultValueOptions,
    submit,
    handlerPool,
    deviceWidth,
    messages,
    modalImageCropper,
    setModalImageCropper,
    handleVerifyRecaptcha,
    recaptcha,
    refreshRecaptcha,
    setRefreshRecaptcha,
    handlerCropImage,
    getCropperSize,
    toggleModalImageCropper,
    handleModerateMessage,
    handleVerifySlug,
    isValidSlug,
    setSelectedCategoryId,
    selectedCategoryId,
    handlerEditPool,
    handleFinishCampaign,
    /**Form */
    formData,
    setFormData,
    snapData,
    setSnapData,
    /** title styles */
    handlerTitleStyles,
    titleStyles,
    formatEventStyle,
    reloadPoolData,
  };
};
